import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import useAuth from '../hooks/useAuth';
import useTenant from '../hooks/useTenant';
import { obterCapabilitiesMe } from '../api/capabilities';

const CapabilityContext = createContext(null);

/**
 * Armazena as capabilities do usuário autenticado no tenant selecionado.
 * Chama GET /api/v1/capabilities/me?tenantSlug={slug} após login.
 *
 * Fallback gracioso: quando o endpoint falha, `isError = true` e `hasCapability`
 * retorna `true` para tudo — o sidebar exibe todos os itens sem filtrar.
 */
function CapabilityProvider({ children }) {
  const { isAuthenticated, roleGlobal } = useAuth();
  const { selectedSlug } = useTenant();
  const [capabilities, setCapabilities] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);

  const isMaster = roleGlobal === 'Master';

  const refreshCapabilities = useCallback(async () => {
    if (!isAuthenticated) {
      setCapabilities([]);
      setIsLoaded(false);
      setIsError(false);
      return;
    }
    if (isMaster) {
      setCapabilities([]);
      setIsLoaded(true);
      setIsError(false);
      return;
    }
    if (!selectedSlug) return;

    setIsLoaded(false);
    setIsError(false);
    try {
      const res = await obterCapabilitiesMe(selectedSlug);
      const data = res.data?.data ?? res.data;
      setCapabilities(Array.isArray(data) ? data : []);
    } catch {
      setCapabilities([]);
      setIsError(true);
    } finally {
      setIsLoaded(true);
    }
  }, [isAuthenticated, selectedSlug, isMaster]);

  // Sempre que mudar a autenticação ou o tenant selecionado, recarrega as capabilities
  useEffect(() => {
    refreshCapabilities();
  }, [refreshCapabilities]);

  const hasCapability = (code) => {
    if (isMaster) return true; // Master tem todas as capabilities
    if (isError) return true; // fallback gracioso: endpoint indisponível
    if (!isLoaded) return false; // ainda carregando — não exibir nada
    return capabilities.includes(code);
  };

  return (
    <CapabilityContext.Provider
      value={{ capabilities, isLoaded, isError, hasCapability, isMaster, refreshCapabilities }}
    >
      {children}
    </CapabilityContext.Provider>
  );
}

function useCapabilityContext() {
  const ctx = useContext(CapabilityContext);
  if (!ctx) throw new Error('CapabilityContext must be inside CapabilityProvider');
  return ctx;
}

export { CapabilityProvider, useCapabilityContext, CapabilityContext };
